import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

let hadKeyboardEvent = false;
document.addEventListener('keydown', () => { hadKeyboardEvent = true; }, true);
document.addEventListener('mousedown', () => { hadKeyboardEvent = false; }, true);
document.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'keyboard') hadKeyboardEvent = false; }, true);

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** @type {boolean} */
  #isOpening = false;

  /**
   * Shows the dialog.
   */
  async showDialog() {
    const { dialog } = this.refs;

    if (dialog?.open || this.#isOpening || this.#isClosing) return;

    this.#isOpening = true;
    const scrollY = window.scrollY;

    try {
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      dialog.classList.add('dialog-opening');
      
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });

      dialog.classList.remove('dialog-opening');
    } catch (err) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: scrollY, behavior: 'instant' });
      dialog.classList.remove('dialog-opening');
      console.error(err)
      throw err;
    } finally {
      dialog.removeEventListener('close', this.#handleClose);
      dialog.addEventListener('close', this.#handleClose);
      this.addEventListener('click', this.#handleClick, true);
      this.#isOpening = false;
      if (!hadKeyboardEvent && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }
  }

  #handleClose = async () => {
    try {
      document.body.style.width = '';
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';

      window.scrollTo({ top: parseInt(scrollY, 10) * -1, behavior: 'instant' });
    } catch(err) {
      console.error(err)
      throw err
    } finally {
      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  /** @type {boolean} */
  #isClosing = false;

  /**
   * Closes the dialog.
   */
  async closeDialog() {
    const { dialog } = this.refs;
    if (!dialog?.open || this.#isClosing || this.#isOpening) return;

    this.#isClosing = true;

    try {
      dialog.classList.add('dialog-closing');
      await onAnimationEnd(dialog, undefined, {
        subtree: false,
      });
      dialog.close();
    } catch(err) {
      dialog.classList.remove('dialog-closing');
      console.error(err)
      throw err
    } finally {
      dialog.classList.remove('dialog-closing');
      this.#isClosing = false;
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   * Search modal: close when user clicks wishlist button so the Swym modal (non-top-layer) is not blocked by the dialog (top layer).
   * Do not close when the click is inside the Swym "Add to list" modal (outside the dialog).
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;
    if (!dialog?.open || this.#isClosing || this.#isOpening) return;

    const isSearchModal = this.classList.contains('search-modal');
    const target = event.target instanceof Element ? event.target : null;

    if (isSearchModal && target?.closest('.resource-card__wishlist-button')) {
      this.closeDialog();
      return;
    }

    if (!isClickedOutside(event, dialog)) return;

    const isClickInsideSwymModal = target?.closest('.swym-modal-content');
    if (isSearchModal && isClickInsideSwymModal) return;

    this.closeDialog();
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    
    const { dialog } = this.refs;
    if (!dialog?.open || this.#isClosing || this.#isOpening) return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDialogElement || event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
